import { useState, type FormEvent } from "react";
import { CATEGORIES, type Transaction, type TransactionInput, type TransactionType } from "../types";
import { centsToInput, yuanToCents } from "../utils/currency";
import { currentTime } from "../utils/date";

interface TransactionFormProps {
  date: string;
  transaction?: Transaction;
  onSubmit: (input: TransactionInput) => Promise<void>;
  onCancel: () => void;
}

export function TransactionForm({ date, transaction, onSubmit, onCancel }: TransactionFormProps) {
  const [type, setType] = useState<TransactionType>(transaction?.type ?? "expense");
  const [amount, setAmount] = useState(transaction ? centsToInput(transaction.amount_cents) : "");
  const [category, setCategory] = useState(transaction?.category ?? "餐饮");
  const [note, setNote] = useState(transaction?.note ?? "");
  const [transactionDate, setTransactionDate] = useState(transaction?.transaction_date ?? date);
  const [transactionTime, setTransactionTime] = useState(transaction?.transaction_time ?? currentTime());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const amountCents = yuanToCents(amount);
    if (!amountCents) {
      setError("请输入大于 0、最多两位小数的金额。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit({
        type,
        amount_cents: amountCents,
        category,
        note: note.trim(),
        transaction_date: transactionDate,
        transaction_time: transactionTime,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "保存失败，请稍后重试。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="transaction-form" onSubmit={handleSubmit}>
      <fieldset className="type-switch">
        <legend>类型</legend>
        <div>
          <button type="button" className={type === "expense" ? "selected expense" : ""} onClick={() => setType("expense")}>支出</button>
          <button type="button" className={type === "income" ? "selected income" : ""} onClick={() => setType("income")}>收入</button>
        </div>
      </fieldset>

      <label className="amount-field">
        <span>金额</span>
        <span className="currency-input"><b>¥</b><input autoFocus inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" required /></span>
      </label>

      <label>
        <span>分类</span>
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>

      <label>
        <span>备注</span>
        <input maxLength={120} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：午餐" />
      </label>

      <div className="form-row">
        <label><span>日期</span><input type="date" value={transactionDate} onChange={(event) => setTransactionDate(event.target.value)} required /></label>
        <label><span>时间</span><input type="time" value={transactionTime} onChange={(event) => setTransactionTime(event.target.value)} required /></label>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="form-actions">
        <button className="secondary-button" type="button" onClick={onCancel}>取消</button>
        <button className="primary-button" type="submit" disabled={saving}>{saving ? "正在保存…" : transaction ? "保存修改" : "添加账目"}</button>
      </div>
    </form>
  );
}
